import { useState, useMemo, useEffect } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme } from '@/contexts/ThemeContext'
import { usePermissions } from '@/hooks/usePermissions'
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts'
import { GlobalSearch } from '@/components/common/GlobalSearch'
import { NotificationBell } from '@/components/common/NotificationBell'
import { cn } from '@/lib/utils'
import { EcosystemNavbar } from '@/components/common/EcosystemNavbar'
import {
  Activity,
  LayoutDashboard,
  FileText,
  Package,
  Receipt,
  Calculator,
  Menu,
  X,
  LogOut,
  ChevronLeft,
  Sun,
  Moon,
  Monitor,
  Anchor,
  Ship,
  MapPin,
  Calendar,
  FileSpreadsheet,
  Settings,
  Bell,
  Mail,
  Briefcase,
  DollarSign,
  Database,
  ChevronDown,
  ChevronRight,
  CheckSquare,
  TrendingUp,
  Percent,
  BarChart3,
  Users,
  Upload,
  ClipboardList,
  Truck,
  Zap,
  Building2,
  Cog,
  ListChecks,
  ShieldCheck,
  Navigation,
  FileCode,
  Shield,
  KeyRound,
  ScrollText,
  HeartPulse,
  Link2,
  ShoppingBag,
  MessageSquare,
  User,
  CreditCard,
  Hash,
  Wrench,
  Search,
  Command,
  Network,
  ExternalLink
} from 'lucide-react'
import { Button } from '@/components/ui/Button'

const navigationGroups = [
  {
    id: 'my-office',
    name: 'My Office',
    icon: User,
    module: 'my_office',
    items: [
      { name: 'My Office', href: '/dashboard/my-office', icon: Briefcase, status: 'live' as const },
      { name: 'Jobs', href: '/dashboard/jobs', icon: ClipboardList, status: 'live' as const },
      { name: 'Service Requests', href: '/dashboard/service-requests', icon: FileText, status: 'live' as const },
      { name: 'Quotations', href: '/dashboard/quotations', icon: DollarSign, status: 'live' as const },
      { name: 'Contracts', href: '/dashboard/my-contracts', icon: ScrollText, status: 'live' as const },
      { name: 'Bookings', href: '/dashboard/bookings', icon: Calendar, status: 'live' as const },
      { name: 'Shipments', href: '/dashboard/shipments', icon: Truck, status: 'live' as const },
      { name: 'Bills of Lading', href: '/dashboard/bl', icon: FileText, status: 'live' as const },
      { name: 'Invoices', href: '/dashboard/invoices', icon: Receipt, status: 'live' as const },
      { name: 'Containers', href: '/dashboard/containers', icon: Package, status: 'live' as const },
      { name: 'Arrival Notices', href: '/dashboard/arrival-notices', icon: Mail, status: 'live' as const },
      { name: 'Transport Documents', href: '/dashboard/transport-documents', icon: Upload, status: 'live' as const },
      { name: 'Delivery Orders', href: '/dashboard/delivery-orders', icon: Truck, status: 'live' as const },
      { name: 'Entities', href: '/dashboard/entities', icon: Building2, status: 'live' as const },
      { name: 'Cheques', href: '/dashboard/cheques', icon: FileText, status: 'live' as const },
      { name: 'SOA', href: '/dashboard/soa', icon: FileSpreadsheet, status: 'live' as const },
      { name: 'Payments', href: '/dashboard/payments', icon: CreditCard, status: 'live' as const },
      { name: 'Finance Dashboard', href: '/dashboard/finance', icon: BarChart3, status: 'live' as const }
    ]
  },
  {
    id: 'operations',
    name: 'Operations',
    icon: Briefcase,
    module: 'operations',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, status: 'live' as const },
      { name: 'Control Tower', href: '/dashboard/control-tower', icon: Activity, status: 'live' as const },
      { name: 'SLA Monitor', href: '/dashboard/sla-monitor', icon: Bell, status: 'live' as const },
      { name: 'Forms & Documents', href: '/dashboard/forms', icon: FileText, status: 'live' as const }
    ]
  },
  {
    id: 'finance-accounting',
    name: 'Finance & Accounting',
    icon: DollarSign,
    module: 'finance',
    items: [
      { name: 'Finance Dashboard', href: '/dashboard/finance', icon: BarChart3, status: 'live' as const },
      { name: 'Invoices', href: '/dashboard/invoices', icon: Receipt, status: 'live' as const },
      { name: 'Payments', href: '/dashboard/payments', icon: CreditCard, status: 'live' as const },
      { name: 'SOA', href: '/dashboard/soa', icon: FileSpreadsheet, status: 'live' as const },
      { name: 'Freight Charges', href: '/dashboard/freight-charges', icon: DollarSign, status: 'live' as const },
      { name: 'Credit Control', href: '/dashboard/credit-control', icon: Users, status: 'live' as const },
      { name: 'Demurrage', href: '/dashboard/demurrage', icon: Calculator, status: 'live' as const },
      { name: 'Cheques', href: '/dashboard/cheques', icon: FileText, status: 'live' as const },
      { name: 'Payment Types', href: '/dashboard/payment-types', icon: CreditCard, status: 'live' as const },
      { name: 'ROE', href: '/dashboard/roe', icon: TrendingUp, status: 'live' as const },
      { name: 'Currencies', href: '/dashboard/currencies', icon: DollarSign, status: 'live' as const },
      { name: 'Financial P&L', href: '/dashboard/analytics/financial', icon: TrendingUp, status: 'live' as const }
    ]
  },
  {
    id: 'sales',
    name: 'Sales',
    icon: TrendingUp,
    module: 'sales',
    items: [
      { name: 'Quotations', href: '/dashboard/quotations', icon: FileSpreadsheet, status: 'live' as const },
      { name: 'Rate Cards', href: '/dashboard/rate-cards', icon: DollarSign, status: 'live' as const },
      { name: 'Pricing Engine', href: '/dashboard/pricing', icon: Calculator, status: 'live' as const }
    ]
  },
  {
    id: 'crm',
    name: 'CRM',
    icon: Users,
    module: 'crm',
    items: [
      { name: 'Entities', href: '/dashboard/entities', icon: Building2, status: 'live' as const },
      { name: 'Tasks', href: '/dashboard/tasks', icon: CheckSquare, status: 'live' as const },
      { name: 'Pipeline', href: '/dashboard/pipeline', icon: TrendingUp, status: 'live' as const },
      { name: 'Commissions', href: '/dashboard/commissions', icon: Percent, status: 'live' as const }
    ]
  },
  {
    id: 'analytics',
    name: 'Analytics',
    icon: BarChart3,
    module: 'analytics',
    items: [
      { name: 'Profitability', href: '/dashboard/analytics/profitability', icon: TrendingUp, status: 'live' as const },
      { name: 'Operational KPIs', href: '/dashboard/analytics/operational', icon: TrendingUp, status: 'live' as const },
      { name: 'Reports', href: '/dashboard/reports', icon: BarChart3, status: 'live' as const }
    ]
  },
  {
    id: 'logistics',
    name: 'Logistics',
    icon: Truck,
    module: 'logistics',
    items: [
      { name: 'Delivery Orders', href: '/dashboard/delivery-orders', icon: Truck, status: 'live' as const },
      { name: 'Voyages', href: '/dashboard/voyages', icon: Ship, status: 'live' as const },
      { name: 'Lines', href: '/dashboard/lines', icon: Ship, status: 'live' as const },
      { name: 'Vessel Types', href: '/dashboard/vessel-types', icon: Ship, status: 'live' as const },
      { name: 'Cargo Types', href: '/dashboard/cargo-types', icon: Package, status: 'live' as const },
      { name: 'Package Types', href: '/dashboard/package-types', icon: Package, status: 'live' as const },
      { name: 'Incoterms', href: '/dashboard/incoterms', icon: FileText, status: 'live' as const },
      { name: 'HS Codes', href: '/dashboard/hscodes', icon: Hash, status: 'live' as const },
      { name: 'Services', href: '/dashboard/services', icon: Wrench, status: 'live' as const },
      { name: 'Communications', href: '/dashboard/communications', icon: Mail, status: 'live' as const }
    ]
  },
  {
    id: 'notifications',
    name: 'Notifications',
    icon: Bell,
    module: 'notifications_config',
    items: [
      { name: 'Templates', href: '/dashboard/notification-templates', icon: Mail, status: 'live' as const },
      { name: 'Rules', href: '/dashboard/notification-rules', icon: Zap, status: 'live' as const }
    ]
  },
  {
    id: 'master-data',
    name: 'Master Data',
    icon: Database,
    module: 'master_data',
    items: [
      { name: 'Ports', href: '/dashboard/ports', icon: MapPin, status: 'live' as const },
      { name: 'Airports', href: '/dashboard/airports', icon: Navigation, status: 'live' as const },
      { name: 'Countries', href: '/dashboard/countries', icon: MapPin, status: 'live' as const },
      { name: 'Vessels', href: '/dashboard/vessels', icon: Ship, status: 'live' as const },
      { name: 'Shipping Lines', href: '/dashboard/shipping-lines', icon: Ship, status: 'live' as const },
      { name: 'Trade Lanes', href: '/dashboard/trade-lanes', icon: Anchor, status: 'live' as const },
      { name: 'Tariffs', href: '/dashboard/tariffs', icon: FileSpreadsheet, status: 'live' as const },
      { name: 'Free Trade Zones', href: '/dashboard/free-trade-zones', icon: MapPin, status: 'live' as const },
      { name: 'Dangerous Goods', href: '/dashboard/dangerous-goods', icon: Package, status: 'live' as const },
      { name: 'Packaging Types', href: '/dashboard/packaging-types', icon: Package, status: 'live' as const },
      { name: 'Surcharges', href: '/dashboard/surcharges', icon: DollarSign, status: 'live' as const },
      { name: 'Document Types', href: '/dashboard/document-types', icon: FileText, status: 'live' as const },
      { name: 'Commodities', href: '/dashboard/commodity-categories', icon: Package, status: 'live' as const },
      { name: 'Units', href: '/dashboard/units', icon: Calculator, status: 'live' as const },
      { name: 'Payment Terms', href: '/dashboard/payment-terms', icon: DollarSign, status: 'live' as const },
      { name: 'Import Wizard', href: '/dashboard/import/wizard', icon: Upload, status: 'live' as const }
    ]
  },
  {
    id: 'automation',
    name: 'Automation',
    icon: Cog,
    module: 'automation',
    items: [
      { name: 'Workflow Registry', href: '/dashboard/workflow-registry', icon: Network, status: 'live' as const },
      { name: 'Workflow Templates', href: '/dashboard/workflow-templates', icon: Activity, status: 'live' as const },
      { name: 'Workflow Dashboard', href: '/dashboard/workflow-dashboard', icon: Activity, status: 'live' as const },
      { name: 'Workflow Builder', href: '/dashboard/workflows', icon: Navigation, status: 'live' as const },
      { name: 'Automation Rules', href: '/dashboard/automation/rules', icon: Zap, status: 'live' as const },
      { name: 'Automation Queue', href: '/dashboard/automation/queue', icon: ListChecks, status: 'live' as const },
      { name: 'Credit Approvals', href: '/dashboard/automation/credit-approvals', icon: ShieldCheck, status: 'live' as const }
    ]
  },
  {
    id: 'integration',
    name: 'Integration',
    icon: Database,
    module: 'integration',
    items: [
      { name: 'Container Tracking', href: '/dashboard/tracking', icon: Navigation, status: 'live' as const },
      { name: 'EDI Integration', href: '/dashboard/edi', icon: FileCode, status: 'live' as const }
    ]
  },
  {
    id: 'system',
    name: 'System',
    icon: Monitor,
    module: 'system',
    items: [
      { name: 'OTMS Engine', href: '/dashboard/engine', icon: Activity, status: 'live' as const }
    ]
  },
  {
    id: 'mololink',
    name: 'MoloLink',
    icon: Link2,
    module: 'mololink',
    items: [
      { name: 'MoloLink Home', href: '/dashboard/mololink', icon: Link2, status: 'live' as const },
      { name: 'Marketplace', href: '/dashboard/mololink/marketplace', icon: ShoppingBag, status: 'live' as const },
      { name: 'Network Feed', href: '/dashboard/mololink/feed', icon: MessageSquare, status: 'live' as const },
      { name: 'My Profile', href: 'https://molochain.com/profile', icon: User, status: 'live' as const },
      { name: 'Connections', href: '/dashboard/mololink/connections', icon: Users, status: 'live' as const },
      { name: 'Messages', href: '/dashboard/mololink/messages', icon: Mail, status: 'live' as const },
      { name: 'Companies', href: '/dashboard/mololink/companies', icon: Building2, status: 'live' as const },
      { name: 'Job Board', href: '/dashboard/mololink/jobs', icon: Briefcase, status: 'live' as const },
      { name: 'Notifications', href: '/dashboard/mololink/notifications', icon: Bell, status: 'live' as const }
    ]
  }
]

const adminNavigationGroup = {
  id: 'admin',
  name: 'Administration',
  icon: Shield,
  module: 'admin',
  items: [
    { name: 'User Management', href: '/dashboard/admin/users', icon: Users, status: 'live' as const },
    { name: 'Permission Editor', href: '/dashboard/admin/permissions', icon: KeyRound, status: 'live' as const },
    { name: 'Audit Logs', href: '/dashboard/admin/audit-logs', icon: ScrollText, status: 'live' as const },
    { name: 'Health Monitor', href: '/dashboard/admin/health', icon: HeartPulse, status: 'live' as const },
    { name: 'Platform Settings', href: '/dashboard/admin/settings', icon: Settings, status: 'live' as const }
  ]
}

export function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [themeMenuOpen, setThemeMenuOpen] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    operations: true,
    'finance-accounting': true,
    sales: false,
    crm: false,
    analytics: false,
    logistics: false,
    notifications: false,
    'master-data': false,
    automation: false,
    integration: false,
    system: false,
    mololink: false
  })
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { theme, setTheme } = useTheme()
  const { hasModuleAccess } = usePermissions()

  const activeNavGroups = useMemo(() => {
    const allGroups = [...navigationGroups, adminNavigationGroup]
    return allGroups.filter(group => {
      if (!group.module) return true
      return hasModuleAccess(group.module as any)
    })
  }, [hasModuleAccess])
  
  const { isSearchOpen, setIsSearchOpen } = useKeyboardShortcuts()

  useEffect(() => {
    const path = location.pathname
    for (const group of activeNavGroups) {
      if (group.items.some(item => {
        if (item.href === '/dashboard') return path === '/dashboard'
        return path.startsWith(item.href)
      })) {
        setExpandedGroups(prev => ({ ...prev, [group.id]: true }))
        break
      }
    }
  }, [location.pathname, activeNavGroups])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const isActive = (href: string) => {
    if (href === '/dashboard') return location.pathname === '/dashboard'
    if (href === '/dashboard/mololink') return location.pathname === '/dashboard/mololink'
    return location.pathname.startsWith(href)
  }

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }))
  }

  const getBreadcrumb = () => {
    const path = location.pathname
    const allItems = activeNavGroups.flatMap(g => g.items.map(i => ({ ...i, groupName: g.name })))
    const match = allItems.find(i => {
      if (i.href === '/dashboard') return path === '/dashboard'
      return path.startsWith(i.href)
    })
    return match ? { group: match.groupName, page: match.name } : null
  }

  const breadcrumb = getBreadcrumb()

  return (
    <>
      <GlobalSearch isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
      <EcosystemNavbar />
      <div className="min-h-screen bg-background">
      <header className="sticky top-[56px] z-40 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/95 shadow-sm">
        <div className="flex h-14 items-center gap-4 px-4">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden hover:bg-accent"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="hidden md:flex hover:bg-accent"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <ChevronLeft className={cn('h-5 w-5 transition-transform', !sidebarOpen && 'rotate-180')} />
          </Button>

          <div className="flex items-center gap-2.5">
            <img src="/molochain-logo.jpeg" alt="Molochain" className="h-8 w-auto" />
          </div>

          {breadcrumb && (
            <div className="hidden md:flex items-center gap-1.5 text-sm text-muted-foreground">
              <span>/</span>
              <span>{breadcrumb.group}</span>
              <span>/</span>
              <span className="font-medium text-foreground">{breadcrumb.page}</span>
            </div>
          )}

          <div className="flex-1" />

          <button
            onClick={() => setIsSearchOpen(true)}
            className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-muted/50 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors min-w-[200px]"
          >
            <Search className="h-3.5 w-3.5" />
            <span>Search...</span>
            <kbd className="ml-auto text-[10px] font-mono bg-background border border-border rounded px-1.5 py-0.5">
              <Command className="h-2.5 w-2.5 inline" />K
            </kbd>
          </button>

          <div className="flex items-center gap-1">
            <NotificationBell />

            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="hover:bg-accent"
                onClick={() => setThemeMenuOpen(!themeMenuOpen)}
              >
                {theme === 'light' && <Sun className="h-5 w-5" />}
                {theme === 'dark' && <Moon className="h-5 w-5" />}
                {theme === 'system' && <Monitor className="h-5 w-5" />}
              </Button>

              {themeMenuOpen && (
                <div className="absolute right-0 mt-2 w-40 rounded-lg border bg-popover shadow-lg animate-fade-in">
                  <div className="p-1">
                    <button
                      onClick={() => { setTheme('light'); setThemeMenuOpen(false) }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                        theme === 'light' ? 'bg-accent' : 'hover:bg-accent'
                      )}
                    >
                      <Sun className="h-4 w-4" />
                      Light
                    </button>
                    <button
                      onClick={() => { setTheme('dark'); setThemeMenuOpen(false) }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                        theme === 'dark' ? 'bg-accent' : 'hover:bg-accent'
                      )}
                    >
                      <Moon className="h-4 w-4" />
                      Dark
                    </button>
                    <button
                      onClick={() => { setTheme('system'); setThemeMenuOpen(false) }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                        theme === 'system' ? 'bg-accent' : 'hover:bg-accent'
                      )}
                    >
                      <Monitor className="h-4 w-4" />
                      System
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="hidden sm:flex items-center gap-2 pl-2 ml-1 border-l border-border">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-xs font-semibold text-primary">
                  {user?.name?.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="text-sm">
                <p className="font-medium text-foreground leading-tight">{user?.name}</p>
                <p className="text-[11px] text-muted-foreground leading-tight">{user?.email}</p>
              </div>
            </div>

            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleLogout} 
              title="Logout"
              className="hover:bg-accent hover:text-destructive"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Mobile Sidebar */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-30 md:hidden">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
            <aside className="fixed left-0 top-[112px] bottom-0 w-64 bg-background border-r p-4 overflow-y-auto scrollbar-thin animate-slide-in-right">
              <nav className="space-y-3">
                {activeNavGroups.map((group) => (
                  <div key={group.id} className="space-y-1">
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className="flex items-center gap-2 w-full px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                    >
                      <group.icon className="h-4 w-4" />
                      <span className="flex-1 text-left">{group.name}</span>
                      {expandedGroups[group.id] ? (
                        <ChevronDown className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5" />
                      )}
                    </button>
                    
                    {expandedGroups[group.id] && (
                      <div className="space-y-1 animate-fade-in">
                        {group.items.map((item) => 
                          item.href.startsWith('http') ? (
                            <a
                              key={item.name}
                              href={item.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground hover-lift transition-smooth"
                            >
                              <item.icon className="h-4 w-4 flex-shrink-0" />
                              <span className="flex-1">{item.name}</span>
                              <ExternalLink className="h-3 w-3 opacity-50" />
                            </a>
                          ) : (
                            <Link
                              key={item.name}
                              to={item.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className={cn(
                                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-smooth',
                                isActive(item.href)
                                  ? 'bg-primary text-primary-foreground shadow-md'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground hover-lift'
                              )}
                            >
                              <item.icon className="h-4 w-4 flex-shrink-0" />
                              <span className="flex-1">{item.name}</span>
                            </Link>
                          )
                        )}
                      </div>
                    )}
                  </div>
                ))}

                <div className="pt-4 mt-4 border-t">
                  <Link
                    to="/dashboard/settings"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-smooth"
                  >
                    <Settings className="h-6 w-6 flex-shrink-0" />
                    Settings
                  </Link>
                </div>
              </nav>
            </aside>
          </div>
        )}

        {/* Desktop Sidebar - Modern Design */}
        <aside
          className={cn(
            'hidden md:block border-r bg-card transition-all duration-300 ease-in-out',
            sidebarOpen ? 'w-64' : 'w-16'
          )}
        >
          <div className="sticky top-[112px] h-[calc(100vh-7rem)] overflow-y-auto scrollbar-thin p-4">
            <nav className="space-y-3">
              {activeNavGroups.map((group) => (
                <div key={group.id} className="space-y-1">
                  {sidebarOpen ? (
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className="flex items-center gap-2 w-full px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                    >
                      <group.icon className="h-4 w-4" />
                      <span className="flex-1 text-left">{group.name}</span>
                      {expandedGroups[group.id] ? (
                        <ChevronDown className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5" />
                      )}
                    </button>
                  ) : (
                    <div className="flex items-center justify-center py-1.5" title={group.name}>
                      <group.icon className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  
                  {(expandedGroups[group.id] || !sidebarOpen) && (
                    <div className="space-y-1">
                      {group.items.map((item) => 
                        item.href.startsWith('http') ? (
                          <a
                            key={item.name}
                            href={item.href}
                            className="flex items-center gap-2.5 rounded-md px-3 py-1.5 text-[13px] font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-smooth"
                            title={!sidebarOpen ? item.name : undefined}
                          >
                            <item.icon className="h-4 w-4 flex-shrink-0" />
                            {sidebarOpen && (
                              <>
                                <span className="flex-1">{item.name}</span>
                                <ExternalLink className="h-3 w-3 opacity-50" />
                              </>
                            )}
                          </a>
                        ) : (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={cn(
                              'flex items-center gap-2.5 rounded-md px-3 py-1.5 text-[13px] font-medium transition-smooth',
                              isActive(item.href)
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                            )}
                            title={!sidebarOpen ? item.name : undefined}
                          >
                            <item.icon className="h-4 w-4 flex-shrink-0" />
                            {sidebarOpen && (
                              <span className="flex-1">{item.name}</span>
                            )}
                          </Link>
                        )
                      )}
                    </div>
                  )}
                </div>
              ))}

              <div className={cn('pt-4 mt-4 border-t', !sidebarOpen && 'border-transparent')}>
                <Link
                  to="/dashboard/settings"
                  className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-smooth hover-lift"
                  title={!sidebarOpen ? 'Settings' : undefined}
                >
                  <Settings className="h-6 w-6 flex-shrink-0" />
                  {sidebarOpen && <span className="animate-fade-in">Settings</span>}
                </Link>
              </div>
            </nav>
          </div>
        </aside>

        {/* Main Content - Modern Spacing */}
        <main className="flex-1 p-6 md:p-8 overflow-x-hidden">
          <div className="max-w-7xl mx-auto animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
    </>
  )
}
